import { motion } from "framer-motion";
import { ArrowRight, Globe, LayoutDashboard, MousePointerClick, Puzzle, ShoppingBag, Smartphone } from "lucide-react";
import { SectionHeading, spotlightMove } from "./Reveal";
import { CTAButton } from "./CTAButton";
import { EASE } from "@/lib/site";
import { goToContact } from "@/lib/scroll";

const SERVICES = [
  { n: "01", icon: Globe, title: "Sites Profissionais", text: "Sites modernos, rápidos, responsivos e desenvolvidos para apresentar sua empresa e conquistar novos clientes.", tags: ["Institucional", "Profissional", "SEO"], cta: "Quero criar meu site", type: "Site", span: "lg:col-span-2" },
  { n: "02", icon: MousePointerClick, title: "Landing Pages", text: "Páginas estratégicas focadas em conversão, campanhas e geração de leads.", tags: ["Conversão", "Campanhas"], cta: "Quero uma landing page", type: "Landing Page" },
  { n: "03", icon: ShoppingBag, title: "Lojas Virtuais", text: "E-commerces profissionais para vender produtos e serviços pela internet.", tags: ["E-commerce", "Vendas online"], cta: "Quero minha loja virtual", type: "Loja Virtual" },
  { n: "04", icon: Smartphone, title: "Aplicativos", text: "Aplicativos personalizados para Android e iOS de acordo com a necessidade do seu projeto.", tags: ["Android", "iOS"], cta: "Quero criar meu aplicativo", type: "Aplicativo" },
  { n: "05", icon: LayoutDashboard, title: "Sistemas e Plataformas", text: "Sistemas web e plataformas personalizadas para organizar, automatizar e melhorar processos.", tags: ["Sistemas web", "Automação", "Plataformas"], cta: "Quero criar meu sistema", type: "Sistema" },
];

const ServiceCard = ({ s, i }) => (
  <motion.article
    data-testid={`service-card-${s.n}`}
    onMouseMove={spotlightMove}
    initial={{ opacity: 0, y: 40 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, amount: 0.15 }}
    transition={{ duration: 0.8, delay: (i % 3) * 0.08, ease: EASE }}
    className={`spot-card group flex min-h-[300px] flex-col overflow-hidden rounded-3xl border border-white/[0.07] bg-ws-card p-7 transition-[border-color,transform] duration-500 hover:-translate-y-1 hover:border-ws-lime/35 sm:p-8 ${s.span || ""}`}
  >
    <div className="flex items-start justify-between">
      <motion.span
        whileHover={{ rotate: -8, scale: 1.08 }}
        className="grid h-14 w-14 place-items-center rounded-2xl bg-ws-lime/10 text-ws-lime ring-1 ring-ws-lime/25 transition-[box-shadow,background-color] duration-300 group-hover:bg-ws-lime group-hover:text-ws-bg group-hover:shadow-[0_0_36px_-4px_rgba(124,255,26,.75)]"
      >
        <s.icon size={24} className="icon-bob" style={{ animationDelay: `${i * 0.5}s` }} />
      </motion.span>
      <span className="font-display text-5xl font-semibold text-outline transition-colors duration-500 group-hover:text-outline-lime">{s.n}</span>
    </div>
    <h3 className="mt-8 text-xl font-medium tracking-tight text-ws-text sm:text-2xl">{s.title}</h3>
    <p className="mt-3 max-w-md text-[15px] leading-relaxed text-ws-muted">{s.text}</p>
    <div className="mt-5 flex flex-wrap gap-2">
      {s.tags.map((t) => (
        <span key={t} className="rounded-full border border-white/10 px-3 py-1 font-mono text-[11px] text-ws-muted">{t}</span>
      ))}
    </div>
    <button
      data-testid={`service-cta-${s.n}`}
      onClick={() => goToContact(s.type)}
      className="mt-auto flex items-center gap-2 pt-8 text-sm font-semibold text-ws-lime"
    >
      {s.cta}
      <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1.5" />
    </button>
  </motion.article>
);

const CustomCard = () => (
  <motion.article
    data-testid="service-card-06"
    initial={{ opacity: 0, y: 40 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, amount: 0.15 }}
    transition={{ duration: 0.9, ease: EASE }}
    className="relative overflow-hidden rounded-3xl border border-ws-lime/30 bg-ws-lime/[0.05] p-7 sm:p-10 lg:col-span-3"
  >
    <div className="dot-grid mask-radial pointer-events-none absolute inset-0" />
    <div className="spin-slower pointer-events-none absolute -right-24 -top-24 h-80 w-80 rounded-full border border-dashed border-ws-lime/25" />
    <div className="relative flex flex-col gap-8 lg:flex-row lg:items-center lg:justify-between">
      <div className="flex items-start gap-5">
        <span className="grid h-14 w-14 shrink-0 place-items-center rounded-2xl bg-ws-lime text-ws-bg shadow-[0_0_40px_-6px_rgba(124,255,26,.8)]">
          <Puzzle size={24} className="icon-bob" />
        </span>
        <div>
          <p className="font-mono text-xs text-ws-lime">06 — Soluções Personalizadas</p>
          <h3 className="mt-2 text-2xl font-medium tracking-tight sm:text-3xl">Tem uma ideia diferente?</h3>
          <p className="mt-3 max-w-xl text-[15px] leading-relaxed text-ws-muted">Criamos uma solução digital específica para sua necessidade.</p>
        </div>
      </div>
      <CTAButton testid="service-cta-06" className="shrink-0" onClick={() => goToContact("Solução personalizada")}>
        Começar meu projeto
      </CTAButton>
    </div>
  </motion.article>
);

export const Services = () => (
  <section id="servicos" data-testid="services-section" className="relative py-24 sm:py-32">
    <div className="mx-auto max-w-7xl px-5 sm:px-8">
      <SectionHeading index="02" eyebrow="O que fazemos" title="O que podemos criar para o seu negócio?" sub="Do primeiro site a uma plataforma completa: cada projeto é pensado para o seu objetivo." testid="services-title" />
      <div className="mt-16 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
        {SERVICES.map((s, i) => (
          <ServiceCard key={s.n} s={s} i={i} />
        ))}
        <CustomCard />
      </div>
    </div>
  </section>
);
